import { Controller } from "@hotwired/stimulus"
import * as echarts from "echarts"

export default class extends Controller {
  static values = {
    points: Array,
    url: String
  }

  connect() {
    this.handleResize = this.resize.bind(this)
    window.addEventListener("resize", this.handleResize)

    if (this.hasPointsValue && this.pointsValue.length > 0) {
      this.render()
      return
    }

    this.renderLoadingState()
    this.observeVisibility()
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResize)
    this.visibilityObserver?.disconnect()
    this.disposeChart()
  }

  observeVisibility() {
    if (!this.hasUrlValue) {
      this.pointsValue = []
      this.render()
      return
    }

    this.visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || this.hasLoaded) return
        this.hasLoaded = true
        this.visibilityObserver?.disconnect()
        this.fetchPoints()
      })
    }, { rootMargin: "200px" })

    this.visibilityObserver.observe(this.element)
  }

  async fetchPoints() {
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`Failed to load history: ${response.status}`)

      const payload = await response.json()
      this.pointsValue = Array.isArray(payload?.points) ? payload.points : []
    } catch (_) {
      this.pointsValue = []
    }

    this.render()
  }

  renderLoadingState() {
    this.element.innerHTML = '<div class="text-xs text-base-content/40 h-full flex items-center justify-center"><span class="loading loading-spinner loading-xs mr-2"></span>Loading</div>'
  }

  render() {
    const seriesEntries = this.seriesEntries()
    const totalPoints = seriesEntries.reduce((count, [, points]) => count + points.length, 0)

    if (totalPoints <= 1) {
      this.disposeChart()
      this.element.innerHTML = '<div class="text-xs text-base-content/50 h-full flex items-center justify-center">—</div>'
      return
    }

    this.element.innerHTML = ""
    this.chart ||= echarts.init(this.element)

    const hasMultipleUnitMeasures = seriesEntries.length > 1
    const palette = this.seriesPalette()
    this.chart.setOption({
      animation: false,
      legend: {
        show: hasMultipleUnitMeasures,
        top: 0,
        left: "center",
        itemWidth: 10,
        itemHeight: 6,
        textStyle: {
          color: "rgba(100, 116, 139, 0.9)",
          fontSize: 9
        }
      },
      grid: { left: 16, right: 18, top: hasMultipleUnitMeasures ? 30 : 16, bottom: 34, containLabel: true },
      tooltip: { show: false },
      xAxis: {
        type: "time",
        axisLine: { show: true, lineStyle: { color: "rgba(148, 163, 184, 0.45)" } },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: {
          show: true,
          hideOverlap: true,
          color: "rgba(100, 116, 139, 0.9)",
          fontSize: 9,
          formatter: (value) => this.shortDate(value)
        }
      },
      yAxis: {
        type: "value",
        scale: true,
        splitNumber: 3,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: true, lineStyle: { color: "rgba(148, 163, 184, 0.2)" } },
        axisLabel: { show: false }
      },
      series: [
        ...seriesEntries.map(([unitMeasure, points], index) => ({
          name: unitMeasure,
          type: "line",
          silent: true,
          clip: false,
          data: points,
          showSymbol: true,
          symbolSize: 4,
          smooth: true,
          emphasis: { disabled: true },
          label: {
            show: true,
            position: "top",
            color: palette[index % palette.length],
            fontSize: 9,
            formatter: ({ value }) => this.compactCurrency(value?.[1])
          },
          lineStyle: {
            width: 1.5,
            color: palette[index % palette.length]
          },
          areaStyle: {
            color: palette[index % palette.length],
            opacity: 0.05
          }
        }))
      ]
    })
  }

  resize() {
    if (this.chart) this.chart.resize()
  }

  disposeChart() {
    if (!this.chart) return

    this.chart.dispose()
    this.chart = null
  }

  seriesEntries() {
    if (!Array.isArray(this.pointsValue)) return []

    const groupedPoints = this.pointsValue.reduce((accumulator, point) => {
      if (!point || !point.timestamp || point.amount === null || point.amount === undefined) return accumulator

      const amount = Number(point.amount)
      if (!Number.isFinite(amount)) return accumulator

      const unitMeasure = String(point.unit_measure || "N/A").trim() || "N/A"
      accumulator[unitMeasure] ||= []
      accumulator[unitMeasure].push([point.timestamp, amount])
      return accumulator
    }, {})

    return Object.entries(groupedPoints).map(([unitMeasure, points]) => (
      [unitMeasure, points.sort((left, right) => new Date(left[0]) - new Date(right[0]))]
    ))
  }

  currencyFormatter() {
    this._currencyFormatter ||= new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" })
    return this._currencyFormatter
  }

  compactCurrency(value) {
    if (!Number.isFinite(Number(value))) return ""
    return this.currencyFormatter().format(Number(value))
  }

  shortDate(timestamp) {
    return new Date(timestamp).toLocaleDateString(undefined, { month: "numeric", day: "numeric", year: "2-digit" })
  }

  seriesPalette() {
    return [
      this.resolveThemeColor(),
      "#10b981",
      "#f59e0b",
      "#8b5cf6",
      "#ef4444",
      "#06b6d4"
    ]
  }

  resolveThemeColor() {
    const probe = document.createElement("span")
    probe.className = "text-primary"
    probe.style.position = "absolute"
    probe.style.left = "-9999px"
    probe.style.top = "-9999px"
    probe.style.visibility = "hidden"
    probe.textContent = "x"
    document.body.appendChild(probe)

    const color = window.getComputedStyle(probe).color
    probe.remove()
    return color || "#3b82f6"
  }
}
